// src/api/memberVisibilityApi.ts
// Member Visibility API — assignment of selected records to members
//
// Goes through the CRM BFF (`/api/platform/members/:userId/visibility`), which
// forwards to Central with the server-held, workspace-scoped token. It used to
// call Central browser-direct, which cannot work in production RP/BFF mode:
// the browser holds no Central token there. The workspace is the SESSION's —
// `workspaceId` below only keys the cache.
// Used when a member's visibility level is 'selected' to assign specific records.

import { baseApi } from './baseApi';

// ============================================================
// Types
// ============================================================

/**
 * Visibility level for a domain.
 */
export type VisibilityLevel = 'self' | 'assigned' | 'selected' | 'workspace' | 'none';

/**
 * A single record assignment.
 */
export interface VisibilityAssignment {
  id: string;
  name: string;
}

/**
 * Visibility configuration for a domain/entity.
 */
export interface DomainVisibility {
  level: VisibilityLevel;
  /** Record IDs when level is 'selected' */
  ids?: string[];
  /** Resolved record details (if Central returns them) */
  records?: VisibilityAssignment[];
}

/**
 * Member visibility response from Central.
 * Keyed by app, then by entity.
 */
export interface MemberVisibilityResponse {
  membershipId: string;
  visibility: {
    [appId: string]: {
      [entity: string]: DomainVisibility;
    };
  };
}

/**
 * Request to set/update visibility for a domain.
 */
export interface SetVisibilityRequest {
  workspaceId: string;
  membershipId: string;
  app: string;
  entity: string;
  level: VisibilityLevel;
  ids?: string[];
}

/**
 * Request to add records to selected visibility.
 */
export interface AddAssignmentsRequest {
  workspaceId: string;
  membershipId: string;
  app: string;
  entity: string;
  ids: string[];
}

/**
 * Request to remove records from selected visibility.
 */
export interface RemoveAssignmentsRequest {
  workspaceId: string;
  membershipId: string;
  app: string;
  entity: string;
  ids: string[];
}

// ============================================================
// Supported entity configurations (UI-friendly labels)
// ============================================================

export interface EntityConfig {
  app: string;
  entity: string;
  label: string;
  pluralLabel: string;
  /** Name of the API endpoint to fetch available records */
  searchEndpoint?: string;
}

/**
 * Supported entities for selected visibility.
 * Only these entities show assignment UI.
 */
export const SUPPORTED_ENTITIES: EntityConfig[] = [
  {
    app: 'scheduler',
    entity: 'calendars',
    label: 'Selected Calendar',
    pluralLabel: 'Selected Calendars',
  },
  {
    app: 'crm',
    entity: 'projects',
    label: 'Shared Project',
    pluralLabel: 'Shared Projects',
    searchEndpoint: '/projects',
  },
];

/**
 * Get entity config by app and entity key.
 */
export function getEntityConfig(app: string, entity: string): EntityConfig | undefined {
  return SUPPORTED_ENTITIES.find((e) => e.app === app && e.entity === entity);
}

// ============================================================
// API Definition
// ============================================================

export const memberVisibilityApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['MemberVisibility'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /**
       * Get a member's visibility configuration.
       * GET /api/platform/members/:userId/visibility
       */
      getMemberVisibility: builder.query<
        MemberVisibilityResponse,
        { workspaceId: string; membershipId: string }
      >({
        query: ({ membershipId }) => ({
          url: `/platform/members/${encodeURIComponent(membershipId)}/visibility`,
          method: 'GET',
        }),
        providesTags: (result, error, { membershipId }) => [
          { type: 'MemberVisibility', id: membershipId },
        ],
      }),

      /**
       * Add/remove selected records.
       * PATCH /api/platform/members/:userId/visibility
       */
      updateMemberVisibility: builder.mutation<
        MemberVisibilityResponse,
        { workspaceId: string; membershipId: string; app: string; entity: string; add?: string[]; remove?: string[] }
      >({
        query: ({ membershipId, app, entity, add, remove }) => ({
          url: `/platform/members/${encodeURIComponent(membershipId)}/visibility`,
          method: 'PATCH',
          body: { app, entity, add, remove },
        }),
        invalidatesTags: (result, error, { membershipId }) => [
          { type: 'MemberVisibility', id: membershipId },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetMemberVisibilityQuery,
  useUpdateMemberVisibilityMutation,
} = memberVisibilityApi;

// ============================================================
// Helper: Extract entity visibility from response
// ============================================================

/**
 * Extract visibility for a specific app/entity from the response.
 */
export function extractEntityVisibility(
  response: MemberVisibilityResponse | undefined,
  app: string,
  entity: string
): DomainVisibility | undefined {
  if (!response?.visibility) return undefined;
  return response.visibility[app]?.[entity];
}

/**
 * Check if an entity has 'selected' visibility.
 */
export function hasSelectedVisibility(
  response: MemberVisibilityResponse | undefined,
  app: string,
  entity: string
): boolean {
  const visibility = extractEntityVisibility(response, app, entity);
  return visibility?.level === 'selected';
}

/**
 * Get assigned IDs for a selected visibility entity.
 */
export function getAssignedIds(
  response: MemberVisibilityResponse | undefined,
  app: string,
  entity: string
): string[] {
  const visibility = extractEntityVisibility(response, app, entity);
  if (visibility?.level !== 'selected') return [];
  return visibility.ids || [];
}
